import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationExtras,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { CommonHelper } from '@meplis/common';
import { AuthenticationService } from '../application/authentication.service';
import { AppRoutes } from '@cnp/common';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard {
  private _authService = inject(AuthenticationService);
  private _router = inject(Router);

  private readonly _isBrowser: boolean;

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this._isBrowser = isPlatformBrowser(platformId);
  }

  public async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    // On the server, skip auth check entirely — let SSR render the route shell.
    // The client guard will handle actual auth verification after hydration.
    if (!this._isBrowser) {
      return true;
    }

    if (!this._authService.isLogged) {
      await this._authService.verifyIfIsLoggedIn(null, false);
    }

    if (this._authService.isLogged === true) {
      return true;
    }

    // Guest hitting a login-only hub tab (feed, courses, resources, …): instead
    // of bouncing to the login page, send them to the hub root and let it apply
    // the same rule as landing on "/" — it lands the guest on the first public
    // tab (Events or About) rather than a dead end. Query params (e.g.
    // inviteCode) are carried over so the follow/invite flow keeps working.
    const hubRoot = this._resolveHubRoot(route);
    if (hubRoot) {
      const tree = this._router.parseUrl(hubRoot);
      tree.queryParams = { ...route.queryParams };
      return tree;
    }

    const fullPath = state.url.split('?')[0].replace(/^\//, '');

    const extras: NavigationExtras = {
      queryParams: {
        routeUrlToRedirect: fullPath,
        ...route.queryParams,
      },
    };
    this._router.navigate(['login'], extras);
    return false;
  }

  /**
   * When the guarded route is a hub tab (routeKey `HUB_*`), builds the hub root
   * URL from the `:slug`/`:id` params so guests can be redirected there instead
   * of to login. Returns undefined for non-hub routes.
   */
  private _resolveHubRoot(route: ActivatedRouteSnapshot): string | undefined {
    const routeKey = route.data?.['routeKey'] as string | undefined;
    if (!routeKey?.startsWith('HUB_')) {
      return undefined;
    }

    const id = this._findParam(route, 'id');
    if (!id) {
      return undefined;
    }
    const slug = this._findParam(route, 'slug') ?? id;

    return (
      '/' +
      CommonHelper.buildFullRouteUrl(
        AppRoutes['HUB'].fullPath,
        [slug, id],
        [':slug', ':id']
      )
    );
  }

  /** Walks up the route tree to find an inherited param (slug/id live on the
   * parent hub route, which non-empty child routes don't inherit by default). */
  private _findParam(
    route: ActivatedRouteSnapshot | null,
    key: string
  ): string | undefined {
    let node = route;
    while (node) {
      const value = node.params?.[key];
      if (value) {
        return value;
      }
      node = node.parent;
    }
    return undefined;
  }
}
